import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import type { CategoryType } from "@/types";
import { hotTags, searchContent } from "@/lib/content";
import { useSeo } from "@/hooks/useSeo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SearchResultRow from "./components/SearchResultRow";

type FilterKind = "all" | CategoryType;

const filterTabs: { value: FilterKind; label: string }[] = [
  { value: "all", label: "全部" },
  { value: "synopsis", label: "剧情介绍" },
  { value: "cast", label: "主演信息" },
  { value: "updates", label: "剧集更新" },
];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const [input, setInput] = useState(query);
  const [kind, setKind] = useState<FilterKind>("all");

  useEffect(() => {
    setInput(query);
    setKind("all");
  }, [query]);

  const results = useMemo(() => searchContent(query, kind), [query, kind]);

  useSeo(
    query ? `搜索「${query}」- 出轨同学会` : "站内搜索 - 出轨同学会",
    query
      ? `出轨同学会影视内容站中与「${query}」相关的剧情介绍、主演信息与剧集更新结果。`
      : "搜索出轨同学会的剧情介绍、主演信息与剧集更新内容。",
    "出轨同学会搜索,出轨同学会剧情搜索,主演搜索,剧集搜索",
  );

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = input.trim();
    setSearchParams(trimmed ? { q: trimmed } : {});
  };

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 py-6 md:px-8 md:py-8 lg:px-12">
      <Breadcrumb items={[{ label: "站内搜索" }]} />

      <header className="mt-5 rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
        <h1 className="font-heading text-2xl font-bold text-foreground-900">站内搜索</h1>
        <p className="mt-2 font-label text-sm text-foreground-500">
          输入关键词，快速查找剧情、演员或剧集更新内容。
        </p>
        <form onSubmit={handleSubmit} className="mt-5 flex flex-col gap-3 sm:flex-row">
          <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-100 px-4 py-2.5">
            <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
              <i className="ri-search-line text-base leading-none"></i>
            </span>
            <input
              value={input}
              onChange={(event) => setInput(event.target.value)}
              type="search"
              placeholder="例如：同学会、天台、陈锐"
              className="w-full bg-transparent font-label text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
              aria-label="搜索关键词"
            />
          </div>
          <button
            type="submit"
            className="whitespace-nowrap rounded-md bg-accent-500 px-6 py-2.5 font-label text-sm font-medium text-primary-950 transition-colors hover:bg-accent-400"
          >
            搜索
          </button>
        </form>
      </header>

      <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_300px] lg:gap-10">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-1 rounded-full border border-background-300 bg-background-50 p-1">
              {filterTabs.map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => setKind(tab.value)}
                  className={`whitespace-nowrap rounded-full px-3.5 py-1.5 font-label text-xs transition-colors ${
                    kind === tab.value
                      ? "bg-primary-700 text-background-50"
                      : "text-foreground-600 hover:bg-background-100"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            {query && (
              <span className="font-label text-xs text-foreground-500">
                关键词「<span className="font-semibold text-foreground-800">{query}</span>」共{" "}
                <span className="font-semibold text-foreground-800">{results.length}</span> 个结果
              </span>
            )}
          </div>

          <div className="mt-5">
            {!query ? (
              <div className="flex flex-col items-center gap-4 rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-search-line text-2xl leading-none"></i>
                </span>
                <h2 className="font-heading text-lg font-semibold text-foreground-900">
                  输入关键词开始搜索
                </h2>
                <p className="max-w-sm font-label text-sm text-foreground-500">
                  可以试试搜索剧集名称、剧情关键词，或者演员与角色名。
                </p>
              </div>
            ) : results.length === 0 ? (
              <div className="flex flex-col items-center gap-4 rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-emotion-sad-line text-2xl leading-none"></i>
                </span>
                <h2 className="font-heading text-lg font-semibold text-foreground-900">
                  没有找到相关内容
                </h2>
                <p className="max-w-md font-label text-sm text-foreground-500">
                  没有与「{query}」匹配的结果。可以尝试更简短的关键词，或点击下方热门标签重新搜索。
                </p>
                <div className="mt-1 flex flex-wrap justify-center gap-2">
                  {hotTags.slice(0, 5).map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => setSearchParams({ q: tag })}
                      className="rounded-full bg-secondary-100 px-3 py-1 font-label text-xs text-secondary-900 transition-colors hover:bg-secondary-200"
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {results.map((result) => (
                  <SearchResultRow key={result.id} result={result} keyword={query} />
                ))}
              </div>
            )}
          </div>
        </div>

        <aside className="flex flex-col gap-6">
          <section className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-semibold text-foreground-900">热门搜索</h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {hotTags.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => setSearchParams({ q: tag })}
                  className="rounded-full bg-secondary-100 px-3 py-1 font-label text-xs text-secondary-900 transition-colors hover:bg-secondary-200"
                >
                  {tag}
                </button>
              ))}
            </div>
          </section>

          <section className="rounded-lg border border-background-200 bg-secondary-50 p-5">
            <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-900">
              <span className="flex h-4 w-4 items-center justify-center text-secondary-700">
                <i className="ri-lightbulb-line text-base leading-none"></i>
              </span>
              搜索小贴士
            </h2>
            <p className="mt-3 font-label text-sm leading-relaxed text-foreground-600">
              搜索结果支持按“剧情介绍 / 主演信息 / 剧集更新”分类筛选，匹配到的关键词会被高亮显示，点击结果卡片可直接进入对应剧集详情页。
            </p>
          </section>
        </aside>
      </div>
    </div>
  );
}